import { logAdminAction } from './auditLogger';
import { captureError } from '../common/sentry';
import { updateManifestAsnsCoupa, createSiteReceiptCoupa } from '../common/helperFunctions/coupa';
import { UpdateAsnProps } from '../api/postQM';
import { mapToReceiptedItems } from '../pages/receive-manifest/details/detail.mapping';
import { AsnStatus, ManifestPackageStatus, ManifestStatus, SsccStatus, TripLegStatus } from '../common/status';
import { AlertColor } from '@mui/material';
import { AlertStyles } from '../hooks/useAlert';
import { CurrentUserProps } from '../hooks/useCurrentUser';
import { variousAsn } from '../common/constants';
import { ManifestProps, ManifestSSCCProps, TripSheetProps } from '../datastore/tripSheets';
import { DatastoreContextProps } from '../hooks/useDatastore';
import { updateAsnStatus } from '../common/helperFunctions/sscc';

import dayjs from 'dayjs';
import { flatMapPackagesAndAsnLines, updateSsccTimeBasedOnStatus, extractASNsFromManifest } from './utils';
import { ReceiptEventPackageProps, ReceiptEventSiteProps, SSCCProps } from '../datastore/ssccs';
import { AdminActionType, EntityType, SelectedManifest, SelectedManifestSSCC } from '../pages/admin-panel/utils/types';
import { persistReceiveManifestSnapshot } from '../api/saveReceiveManifestSnapshot';

type AuditEntityType = 'Manifest' | 'SSCC' | 'ASN';

const toAuditStatus = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  return String(value);
};

const normalizeAuditReason = (reason: string): string => reason?.trim() || 'No reason provided';

const COUPA_RECEIPT_ERROR_MESSAGE = 'Failed to receipt items in Coupa. Please try again.';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const alertUnexpectedError = ({
  alert,
  context,
  error,
  fallback
}: {
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  context: string;
  error: unknown;
  fallback: string;
}) => {
  const message = getErrorMessage(error, fallback);
  console.error(context, error);
  captureError(error, context);
  alert(message, 'error');
};

const findManifestInTripSheet = (
  tripData: TripSheetProps | undefined,
  manifestId: string | undefined | null
): ManifestProps | undefined => {
  if (!tripData || !manifestId) {
    return undefined;
  }

  return tripData.legs.flatMap((leg) => leg.manifests || []).find((manifest) => manifest.id === manifestId);
};

const syncAdminManifestSnapshot = async ({
  tripData,
  manifest,
  triggeredBy,
  context,
  deleted = false
}: {
  tripData: TripSheetProps;
  manifest: ManifestProps;
  triggeredBy?: string | null;
  context: string;
  deleted?: boolean;
}) => {
  const result = await persistReceiveManifestSnapshot({
    tripData,
    manifest,
    triggeredBy: triggeredBy || undefined,
    mode: 'SAVE',
    deleted,
    clientRequestId: `admin:${context}:${manifest.id}:${Date.now()}`
  });

  if (result?.error) {
    const error = new Error(result.error);
    console.info(`Admin manifest Postgres sync skipped for ${context}: ${result.error}`, {
      manifestId: manifest.id,
      tripId: tripData.id,
      deleted
    });
    captureError(error, `adminManifestActions-${context}-snapshotSync`);
  }
};

const buildAuditSnapshot = ({
  entityType,
  entityId,
  tripId,
  legId,
  manifestId,
  ssccId,
  asnId,
  status,
  changesSummary,
  extra
}: {
  entityType: AuditEntityType;
  entityId: string;
  tripId?: string | null;
  legId?: string | null;
  manifestId?: string | null;
  ssccId?: string | null;
  asnId?: string | null;
  status?: unknown;
  changesSummary: string;
  extra?: Record<string, unknown>;
}) => ({
  entityType,
  entityId,
  tripId: tripId ?? null,
  legId: legId ?? null,
  manifestId: manifestId ?? null,
  ssccId: ssccId ?? null,
  asnId: asnId ?? null,
  status: toAuditStatus(status),
  changesSummary,
  ...(extra || {})
});

const recordAdminAction = async ({
  actionType,
  beforeAction,
  afterAction,
  actionBy,
  reason
}: {
  actionType: AdminActionType;
  beforeAction: Record<string, unknown>;
  afterAction: Record<string, unknown>;
  actionBy: string;
  reason: string;
}) => {
  const reasonText = normalizeAuditReason(reason);
  const auditTimestamp = new Date().toISOString();

  await logAdminAction({
    actionType,
    beforeAction: {
      ...beforeAction,
      auditVersion: 'v2'
    },
    afterAction: {
      ...afterAction,
      auditVersion: 'v2',
      auditTimestamp
    },
    actionBy,
    reason: reasonText
  });
};

const onSaveChangesToTripSheet = async ({
  manifest,
  tripData,
  alert,
  store,
  triggeredBy,
  context = 'saveTripSheet'
}: {
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  manifest: ManifestProps;
  tripData: TripSheetProps | undefined;
  store: DatastoreContextProps;
  triggeredBy?: string | null;
  context?: string;
}) => {
  if (!manifest || !tripData) {
    alert('Manifest or trip data is missing. Please reload and try again.', 'error');
    return;
  }

  try {
    const updatedManifest: ManifestProps = manifest;

    const updatedLegs = tripData.legs.map((leg) => {
      const manifestIndex = leg.manifests.findIndex((m) => m.id === updatedManifest.id);
      if (manifestIndex !== -1) {
        return {
          ...leg,
          manifests: leg.manifests.map((existingManifest, index) =>
            index === manifestIndex ? updatedManifest : existingManifest
          )
        };
      }
      return leg;
    });

    const updatedTripSheet = {
      ...tripData,
      legs: updatedLegs
    };

    await store.tripSheets.update(updatedTripSheet);
    void syncAdminManifestSnapshot({
      tripData: updatedTripSheet,
      manifest: updatedManifest,
      triggeredBy,
      context
    });
    alert('Changes saved successfully', 'success');
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error saving trip sheet changes:',
      error,
      fallback: 'Failed to save changes. Please try again.'
    });
  }
};

const handleSsccAsnStatusUpdateForManifest = async ({
  manifest,
  alert,
  store
}: {
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  manifest: ManifestProps | undefined;
  store: DatastoreContextProps;
}) => {
  if (!manifest) {
    alert('No manifest data', 'error');
    return;
  }

  for (const sscc of manifest.ssccs) {
    const foundSscc = await store.ssccs.get(sscc.ssccId);
    if (!foundSscc) {
      console.info(`SSCC with an ID ${sscc.ssccId} not found - ignoring asn update`);
      continue;
    }
    updateAsnStatus(foundSscc, AsnStatus.available);
    await store.ssccs.update(foundSscc);
  }
};

const handleSsccAsnStatusUpdateForSSCC = async ({
  ssccId,
  makeSsccDelivered,
  weight,
  store
}: {
  makeSsccDelivered?: boolean;
  ssccId: string;
  store: DatastoreContextProps;
  weight?: string;
}) => {
  const foundSscc = await store.ssccs.get(ssccId);
  if (!foundSscc) {
    console.info(`SSCC with an ID ${ssccId} not found - ignoring asn update`);
    return;
  }
  updateAsnStatus(foundSscc, AsnStatus.available);

  const finalSscc = makeSsccDelivered
    ? updateSsccTimeBasedOnStatus({
        status: SsccStatus.delivered,
        weight: weight ?? foundSscc.weight ?? '0',
        sscc: foundSscc
      })
    : weight
      ? { ...foundSscc, weight }
      : foundSscc;

  await store.ssccs.update(finalSscc);
};

const markASNCompleted = async ({
  ssccId,
  ssccWeight,
  receiptedEventName,
  receiptedEventSite,
  asn,
  alert
}: {
  ssccId: string;
  ssccWeight?: string;
  receiptedEventName?: string | null;
  receiptedEventSite?: string | null;
  asn: ReceiptEventPackageProps;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
}): Promise<boolean> => {
  return inventoryTransferASN({
    targetSsccId: ssccId,
    targetSsccWeight: ssccWeight,
    targetReceiptedEventName: receiptedEventName,
    targetReceiptedEventSite: receiptedEventSite,
    asn,
    alert
  });
};

const normalizeAsnForComparison = (value?: string | null): string => (value || '').trim().toUpperCase();

// 01
export const addSSCCToManifest = async ({
  selectedManifest,
  selectedSSCCId,
  inventoryTransfer,
  receiveManifestOptionShown,
  ssccWeight,
  actionReason,
  alert,
  store,
  user
}: {
  selectedManifest: SelectedManifest | null;
  selectedSSCCId: string;
  inventoryTransfer: boolean;
  receiveManifestOptionShown?: boolean;
  ssccWeight: string | undefined;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate all required parameters
  if (!selectedManifest) {
    alert('No manifest selected', 'error');
    return false;
  }

  if (!selectedSSCCId) {
    alert('SSCC identifier is missing', 'error');
    return false;
  }

  if (!selectedManifest.tripId) {
    alert('Trip information is missing', 'error');
    return false;
  }

  if (!selectedManifest.legId) {
    alert('Leg information is missing', 'error');
    return false;
  }

  if (!selectedManifest.manifest || !selectedManifest.manifest.id) {
    alert('Manifest information is missing', 'error');
    return false;
  }

  const finalAsnStatus = inventoryTransfer ? selectedManifest.asnStatus : SsccStatus.delivered;

  try {
    // Fetch SSCC data
    const ssccData = await store.ssccs.get(selectedSSCCId);
    if (!ssccData) {
      alert(`SSCC (${selectedSSCCId}) not found in the system`, 'error');
      return false;
    }

    // Verify SSCC is not already in the manifest
    const isAlreadyInManifest = selectedManifest.manifest.ssccs.some((sscc) => sscc.ssccId === selectedSSCCId);
    if (isAlreadyInManifest) {
      alert(`SSCC ${selectedSSCCId} is already in this manifest`, 'error');
      return false;
    }

    // Fetch trip data
    const tripData = await store.tripSheets.get(selectedManifest.tripId);
    if (!tripData) {
      alert(`Trip (${selectedManifest.tripId}) not found in the system`, 'error');
      return false;
    }

    // Verify leg exists
    const legIndex = tripData.legs.findIndex((leg) => leg.id === selectedManifest.legId);
    if (legIndex === -1) {
      alert(`Leg (${selectedManifest.legId}) not found in this trip`, 'error');
      return false;
    }

    // Verify manifest exists in the leg
    const leg = tripData.legs[legIndex];
    const manifestExists = leg.manifests.some((m) => m.id === selectedManifest.manifest.id);
    if (!manifestExists) {
      alert(`Manifest not found in this leg`, 'error');
      return false;
    }

    const ssccBeforeStatus = toAuditStatus(ssccData.status) || SsccStatus.open;

    const asnStatusChanges = (ssccData.receiptedEvents || [])
      .flatMap((event) => event.packages || [])
      .reduce<Record<string, { asn: string; beforeStatus: string; afterStatus: string }>>((acc, pkg) => {
        const asnId = pkg?.asn || pkg?.id;
        if (!asnId) {
          return acc;
        }

        acc[asnId] = {
          asn: asnId,
          beforeStatus: toAuditStatus(pkg.status) || AsnStatus.open,
          afterStatus: finalAsnStatus || AsnStatus.open
        };

        return acc;
      }, {});

    const asnStatusChangesList = Object.values(asnStatusChanges);

    // Capture before state for audit
    const beforeState = buildAuditSnapshot({
      entityType: 'SSCC',
      entityId: selectedSSCCId,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      manifestId: selectedManifest.manifest.id,
      ssccId: selectedSSCCId,
      status: ssccBeforeStatus,
      changesSummary: `SSCC ${selectedSSCCId} will be added to manifest ${selectedManifest.manifest.id}`,
      extra: {
        ssccIds: selectedManifest.manifest.ssccs.map((sscc) => sscc.ssccId),
        ssccWeight: ssccData.weight ?? null,
        asnStatusChanges: asnStatusChangesList,
        tripReference: tripData.reference || selectedManifest.tripId,
        legIndex,
        addedToManifest: true
      }
    });

    const updatedSSCC = updateSsccTimeBasedOnStatus({
      sscc: ssccData,
      status: selectedManifest.ssccStatus as SsccStatus,
      weight: ssccData.weight ?? ssccWeight ?? '0'
    });

    updatedSSCC.receiptedEvents = updatedSSCC.receiptedEvents.map((event: any) => ({
      ...event,
      packages: event.packages.map((pkg: any) => ({
        ...pkg,
        status: finalAsnStatus,
        statusDate: dayjs()
      }))
    }));

    const newlyAddedSSCC: ManifestSSCCProps = {
      ssccId: updatedSSCC.id,
      totalWeight: Number(updatedSSCC.weight),
      packages: flatMapPackagesAndAsnLines({
        originalSSCC: updatedSSCC,
        inventoryTransfer
      })
    };

    const updatedTripData = {
      ...tripData,
      legs: tripData.legs.map((leg) => {
        if (leg.id === selectedManifest.legId) {
          return {
            ...leg,
            manifests: leg.manifests.map((m) => {
              if (m.id === selectedManifest.manifest.id) {
                return {
                  ...m,
                  ssccs: [...m.ssccs, ...[newlyAddedSSCC]]
                };
              }
              return m;
            })
          };
        }

        return leg;
      })
    };

    await store.ssccs.update(updatedSSCC);
    await store.tripSheets.update(updatedTripData);

    if (inventoryTransfer) {
      await markSSCCCompleted({
        selectedManifestSSCC: {
          ...selectedManifest,
          manifest: {
            ...selectedManifest.manifest,
            ssccs: [newlyAddedSSCC]
          },
          ssccId: selectedSSCCId,
          sscc: {
            ssccId: selectedSSCCId,
            sscc: newlyAddedSSCC,
            asns: (updatedSSCC.receiptedEvents || []).flatMap((event) => event.packages || [])
          }
        },
        actionReason: '',
        alert,
        store,
        user,
        tripReference: selectedManifest.tripId,
        onlySendToCoupa: true
      });
    }

    const syncedManifest = findManifestInTripSheet(updatedTripData, selectedManifest.manifest.id);
    if (syncedManifest) {
      void syncAdminManifestSnapshot({
        tripData: updatedTripData,
        manifest: syncedManifest,
        triggeredBy: user.email,
        context: 'addSsccToManifest'
      });
    }

    // Capture after state for audit
    const afterState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: selectedSSCCId,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      manifestId: selectedManifest.manifest.id,
      ssccId: selectedSSCCId,
      status: updatedSSCC.status,
      changesSummary: `Added SSCC ${selectedSSCCId} to manifest ${selectedManifest.manifest.id}`,
      extra: {
        ssccs: [...selectedManifest.manifest.ssccs.map((sscc) => sscc.ssccId), newlyAddedSSCC.ssccId],
        inventoryTransfer,
        ssccWeight: updatedSSCC.weight ?? ssccWeight ?? null,
        asnStatusChanges: asnStatusChangesList,
        tripReference: tripData.reference || selectedManifest.tripId,
        legIndex,
        addedToManifest: true
      }
    });

    // Log audit action
    await recordAdminAction({
      actionType: AdminActionType.SSCC_ADD_TO_MANIFEST,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error adding SSCC to manifest:',
      error,
      fallback: 'Failed to add the SSCC to the manifest. Please try again.'
    });
    return false;
  }
};

// 02
export const markManifestCompleted = async ({
  finalTripAllowedManifest,
  selectedManifest,
  actionReason,
  alert,
  store,
  user,
  onlySendToCoupa
}: {
  finalTripAllowedManifest?: ManifestProps | undefined;
  selectedManifest: SelectedManifest;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
  onlySendToCoupa: boolean;
}): Promise<boolean> => {
  const rawManifest = selectedManifest.manifest;

  if (!rawManifest) {
    alert('Cannot load the manifest. Please reload the page.', 'error');
    return false;
  }

  // Fetch trip data
  const tripData = await store.tripSheets.get(selectedManifest.tripId);
  if (!tripData) {
    alert(`Trip (${selectedManifest.tripId}) not found in the system`, 'error');
    return false;
  }

  const legIndex = tripData.legs.findIndex((leg) => leg.id === selectedManifest.legId);

  const originalManifestFromTrip = tripData.legs[legIndex]?.manifests?.find(
    (tempManifest) => tempManifest.id === selectedManifest.manifest.id
  );

  if (!originalManifestFromTrip) {
    alert(`Manifest (${selectedManifest.manifest.id}) not found in the system`, 'error');
    return false;
  }

  const finalManifest = finalTripAllowedManifest || originalManifestFromTrip;

  const asnStatusChanges = Array.from(
    new Map(
      (selectedManifest.manifest.ssccsWithAsns || [])
        .flatMap((sscc) => sscc.asns || [])
        .filter((asn) => Boolean(asn?.asn))
        .map((asn) => [
          asn.asn as string,
          {
            asn: asn.asn,
            beforeStatus: toAuditStatus(asn.status) || selectedManifest.asnStatus || AsnStatus.open,
            afterStatus: AsnStatus.available
          }
        ])
    ).values()
  );

  const beforeState = buildAuditSnapshot({
    entityType: EntityType.Manifest,
    entityId: rawManifest.id,
    tripId: selectedManifest.tripId,
    legId: selectedManifest.legId,
    manifestId: rawManifest.id,
    status: selectedManifest.tripDisplayStatus,
    changesSummary: `Manifest ${rawManifest.id} completion started`,
    extra: {
      tripDisplayStatus: selectedManifest.tripDisplayStatus,
      tripReference: tripData.reference || selectedManifest.tripId,
      legIndex,
      asns: extractASNsFromManifest(rawManifest),
      asnStatusChanges
    }
  });

  const manifest = {
    ...finalManifest,
    ssccs: finalManifest.ssccs.map((s) => {
      return {
        ...s,
        packages: s.packages.map((p) => {
          if (p.receivedStatus === ManifestPackageStatus.notReceived) {
            return {
              ...p,
              quantity: p.maxQuantity,
              receivedStatus: ManifestPackageStatus.received
            };
          }

          return p;
        })
      };
    })
  };

  try {
    alert('Submitting to Coupa', 'info');

    const receiptedItems = mapToReceiptedItems(manifest) || [];

    for (const receiptedItem of receiptedItems) {
      const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
      if (receiptResponse.error) {
        alert(COUPA_RECEIPT_ERROR_MESSAGE, 'error');
        console.error(receiptResponse.error);
        return false;
      }
    }

    const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];

    const event: UpdateAsnProps = {
      asns: uniqueAsns.filter((asn): asn is string => asn !== undefined && asn !== null && asn !== variousAsn)
    };

    if (event.asns.length > 0) {
      await updateManifestAsnsCoupa(event);
    }

    manifest.status = ManifestStatus.completed;

    !onlySendToCoupa && alert('Saving changes', 'info');

    !onlySendToCoupa &&
      (await onSaveChangesToTripSheet({
        manifest,
        tripData,
        alert,
        store,
        triggeredBy: user.email,
        context: 'markManifestCompleted'
      }));
    !onlySendToCoupa && (await handleSsccAsnStatusUpdateForManifest({ manifest, alert, store }));

    alert('Manifest submitted successfully', 'success');

    const afterState = buildAuditSnapshot({
      entityType: EntityType.Manifest,
      entityId: manifest.id,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      manifestId: manifest.id,
      status: TripLegStatus.completed,
      changesSummary: `Manifest ${manifest.id} status ${toAuditStatus(selectedManifest.tripDisplayStatus) || 'N/A'} -> ${TripLegStatus.completed}`,
      extra: {
        inventoryTransfer: true,
        tripDisplayStatus: selectedManifest.tripDisplayStatus,
        tripReference: tripData.reference || selectedManifest.tripId,
        legIndex,
        asns: extractASNsFromManifest(rawManifest),
        asnStatusChanges
      }
    });

    await recordAdminAction({
      actionType: AdminActionType.MANIFEST_COMPLETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error completing manifest:',
      error,
      fallback: 'Failed to complete the manifest. Please try again.'
    });
    return false;
  }
};

// 03
export const deleteManifest = async ({
  selectedManifest,
  actionReason,
  alert,
  store,
  user
}: {
  selectedManifest: SelectedManifest | null;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate input
  if (!selectedManifest) {
    alert('No manifest selected for deletion', 'error');
    return false;
  }

  if (!selectedManifest.tripId) {
    alert('Trip information is missing', 'error');
    return false;
  }

  if (!selectedManifest.legId) {
    alert('Leg information is missing', 'error');
    return false;
  }

  if (!selectedManifest.manifest || !selectedManifest.manifest.id) {
    alert('Manifest identifier is missing', 'error');
    return false;
  }

  try {
    // Fetch trip data
    const tripData = await store.tripSheets.get(selectedManifest.tripId);
    if (!tripData) {
      alert(`Trip (${selectedManifest.tripId}) not found in the system`, 'error');
      return false;
    }

    // Verify leg exists
    const legIndex = tripData.legs.findIndex((leg) => leg.id === selectedManifest.legId);
    if (legIndex === -1) {
      alert(`Leg (${selectedManifest.legId}) not found in this trip`, 'error');
      return false;
    }

    const beforeState = buildAuditSnapshot({
      entityType: EntityType.Manifest,
      entityId: selectedManifest.manifest.id,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      manifestId: selectedManifest.manifest.id,
      status: selectedManifest.manifest.status,
      changesSummary: `Manifest ${selectedManifest.manifest.id} deletion started`,
      extra: {
        tripReference: tripData.reference || selectedManifest.tripId,
        legIndex
      }
    });

    // Verify manifest exists in the specified leg
    const leg = tripData.legs[legIndex];
    const manifestExists = leg.manifests.some((m) => m.id === selectedManifest.manifest.id);
    if (!manifestExists) {
      alert(`Manifest not found in this leg. It may have already been deleted.`, 'error');
      return false;
    }

    // Update trip data - remove manifest from the leg
    const updatedTripData = {
      ...tripData,
      legs: tripData.legs.map((currentLeg, index) => {
        if (index === legIndex) {
          return {
            ...currentLeg,
            manifests: currentLeg.manifests.filter(({ id }) => id !== selectedManifest.manifest.id)
          };
        }
        return currentLeg;
      })
    };

    // Save changes to database
    await store.tripSheets.update(updatedTripData);
    void syncAdminManifestSnapshot({
      tripData: updatedTripData,
      manifest: selectedManifest.manifest,
      triggeredBy: user.email,
      context: 'deleteManifest',
      deleted: true
    });

    // Log the action
    const afterState = buildAuditSnapshot({
      entityType: EntityType.Manifest,
      entityId: selectedManifest.manifest.id,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      manifestId: selectedManifest.manifest.id,
      status: 'Deleted',
      changesSummary: `Deleted manifest ${selectedManifest.manifest.id}`,
      extra: {
        deleted: true,
        tripReference: tripData.reference || selectedManifest.tripId,
        legIndex
      }
    });

    await recordAdminAction({
      actionType: AdminActionType.MANIFEST_DELETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error deleting manifest:',
      error,
      fallback: 'Failed to delete the manifest. Please try again.'
    });
    return false;
  }
};

// 04A
export const addASNToSSCC = async ({
  selectedManifestSSCC,
  tripSheets,
  asn,
  ssccId,
  actionReason,
  tripId,
  legId,
  manifestId,
  receiptedEventIndex,
  asnStatus,
  alert,
  store,
  user,
  inventoryTransfer,
  receiveManifestOptionShown
}: {
  selectedManifestSSCC: SelectedManifestSSCC | null;
  tripSheets: TripSheetProps[];
  asn: ReceiptEventPackageProps | null;
  inventoryTransfer: boolean;
  receiveManifestOptionShown?: boolean;
  ssccId: string;
  actionReason: string;
  tripId?: string;
  legId?: string;
  manifestId?: string;
  receiptedEventIndex?: number;
  asnStatus: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate required parameters
  if (!selectedManifestSSCC) {
    alert('No manifest SSCC selected', 'error');
    return false;
  }

  if (!ssccId) {
    alert('SSCC identifier is missing', 'error');
    return false;
  }

  if (!asn) {
    alert('ASN information is missing', 'error');
    return false;
  }

  if (!actionReason?.trim()) {
    alert('Reason for adding ASN is required', 'error');
    return false;
  }

  if (receiptedEventIndex === undefined || receiptedEventIndex === null) {
    alert('Receipt event index is missing', 'error');
    return false;
  }

  const normalizedTargetAsn = normalizeAsnForComparison(asn?.asn);

  // Check if this is for an unassigned SSCC (not in any trip/leg/manifest)
  const isUnassignedSSCC = !tripId || !legId || !manifestId;

  const tripDataForAudit = !isUnassignedSSCC && tripId ? tripSheets.find((trip) => trip.id === tripId) : undefined;
  const legIndexForAudit =
    !isUnassignedSSCC && legId && tripDataForAudit ? tripDataForAudit.legs.findIndex((leg) => leg.id === legId) : null;
  const resolvedLegIndexForAudit =
    typeof legIndexForAudit === 'number' && legIndexForAudit >= 0 ? legIndexForAudit : null;
  const tripReferenceForAudit = tripDataForAudit?.reference || null;

  const beforeState = buildAuditSnapshot({
    entityType: 'ASN',
    entityId: asn.asn || asn.id,
    tripId: tripId || null,
    legId: legId || null,
    manifestId: manifestId || null,
    ssccId,
    asnId: asn.asn || asn.id,
    status: asn.status,
    changesSummary: `ASN ${asn.asn || asn.id} assignment started for SSCC ${ssccId}`,
    extra: {
      receiptedEventIndex,
      isUnassigned: isUnassignedSSCC,
      tripReference: tripReferenceForAudit,
      legIndex: resolvedLegIndexForAudit
    }
  });

  try {
    // Fetch SSCC data
    const ssccData = await store.ssccs.get(ssccId);
    if (!ssccData) {
      alert(`SSCC (${ssccId}) not found in the system`, 'error');
      return false;
    }

    // Verify receiptedEvent exists at the specified index
    if (!ssccData.receiptedEvents || !ssccData.receiptedEvents[receiptedEventIndex]) {
      alert(`Receipt event not found at index ${receiptedEventIndex}`, 'error');
      return false;
    }

    // Check if ASN already exists in this receipt event
    const existingPackage = ssccData.receiptedEvents[receiptedEventIndex].packages?.find(
      (pkg) => normalizeAsnForComparison(pkg.asn) === normalizedTargetAsn || pkg.id === asn.id
    );
    if (existingPackage) {
      alert(`ASN ${asn.asn || asn.id} already exists in this receipt event`, 'error');
      return false;
    }

    const targetAsnStatus = asnStatus;
    const updatedASN = { ...asn, status: targetAsnStatus, statusDate: dayjs() };

    // Update SSCC data - add ASN to the specified receipt event
    const updatedSSCC = {
      ...ssccData,
      receiptedEvents: ssccData.receiptedEvents.map((event: ReceiptEventSiteProps, index: number) => {
        if (index === receiptedEventIndex) {
          return {
            ...event,
            packages: [...(event.packages || []), updatedASN]
          };
        }
        return event;
      })
    };

    // Save SSCC changes
    await store.ssccs.update(updatedSSCC);

    // Prepare ASN data for trip manifest
    const asnForTrip = flatMapPackagesAndAsnLines({
      originalSSCC: { receiptedEvents: [{ packages: [updatedASN] }] } as any,
      inventoryTransfer
    });

    // Update trip data only if SSCC is assigned to a manifest
    if (!isUnassignedSSCC && tripId && legId && manifestId) {
      // Fetch trip data
      const tripData = await store.tripSheets.get(tripId);
      if (!tripData) {
        alert(`Trip (${tripId}) not found in the system`, 'error');
        // SSCC was already updated, but trip update failed
        return false;
      }

      // Verify leg exists
      const legIndex = tripData.legs.findIndex((leg) => leg.id === legId);
      if (legIndex === -1) {
        alert(`Leg (${legId}) not found in this trip`, 'error');
        return false;
      }

      // Update trip data - add ASN to manifest's SSCC
      const updatedTripData = {
        ...tripData,
        legs: tripData.legs.map((leg, index) => {
          if (index === legIndex) {
            return {
              ...leg,
              manifests: leg.manifests.map((m) => {
                if (m.id === manifestId) {
                  return {
                    ...m,
                    ssccs: m.ssccs.map((sscc) => {
                      if (sscc.ssccId === ssccId) {
                        return {
                          ...sscc,
                          packages: [...sscc.packages, ...asnForTrip]
                        };
                      }
                      return sscc;
                    })
                  };
                }
                return m;
              })
            };
          }
          return leg;
        })
      };

      // Save trip data changes
      await store.tripSheets.update(updatedTripData);
      const syncedManifest = findManifestInTripSheet(updatedTripData, manifestId);
      if (syncedManifest) {
        void syncAdminManifestSnapshot({
          tripData: updatedTripData,
          manifest: syncedManifest,
          triggeredBy: user.email,
          context: 'addAsnToSscc'
        });
      }
    }

    if (inventoryTransfer) {
      const targetEvent = updatedSSCC.receiptedEvents[receiptedEventIndex];

      await markASNCompleted({
        ssccId,
        ssccWeight: updatedSSCC.weight ?? undefined,
        receiptedEventName: targetEvent?.name,
        receiptedEventSite: targetEvent?.site,
        asn: updatedASN,
        alert
      });
    }

    // Capture after state for audit
    const afterState = buildAuditSnapshot({
      entityType: EntityType.ASN,
      entityId: updatedASN.asn || updatedASN.id,
      tripId: tripId || null,
      legId: legId || null,
      manifestId: manifestId || null,
      ssccId,
      asnId: updatedASN.asn || updatedASN.id,
      status: targetAsnStatus,
      changesSummary: `Added ASN ${updatedASN.asn || updatedASN.id} to SSCC ${ssccId}`,
      extra: {
        receiptedEventIndex,
        isUnassigned: isUnassignedSSCC,
        added: true,
        inventoryTransfer,
        tripReference: tripReferenceForAudit,
        legIndex: resolvedLegIndexForAudit
      }
    });

    // Log audit action
    await recordAdminAction({
      actionType: AdminActionType.ASN_ASSIGN_TO_SSCC,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error adding ASN to SSCC:',
      error,
      fallback: 'Failed to add ASN to the SSCC. Please try again.'
    });
    return false;
  }
};

// 04B
export const addASNToUnassignedSSCC = async ({
  asn,
  ssccId,
  actionReason,
  receiptedEventIndex,
  asnStatus,
  alert,
  store,
  user
}: {
  asn: ReceiptEventPackageProps | null;
  ssccId: string;
  actionReason: string;
  receiptedEventIndex?: number;
  asnStatus: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate required parameters

  if (!ssccId) {
    alert('SSCC identifier is missing', 'error');
    return false;
  }

  if (!asn) {
    alert('ASN information is missing', 'error');
    return false;
  }

  if (!actionReason?.trim()) {
    alert('Reason for adding ASN is required', 'error');
    return false;
  }

  if (receiptedEventIndex === undefined || receiptedEventIndex === null) {
    alert('Receipt event index is missing', 'error');
    return false;
  }

  const normalizedTargetAsn = normalizeAsnForComparison(asn?.asn);

  const beforeState = buildAuditSnapshot({
    entityType: EntityType.ASN,
    entityId: asn.asn || asn.id,
    ssccId,
    asnId: asn.asn || asn.id,
    status: asn.status,
    changesSummary: `ASN ${asn.asn || asn.id} assignment started for unassigned SSCC ${ssccId}`,
    extra: {
      receiptedEventIndex,
      isUnassigned: true
    }
  });

  try {
    // Fetch SSCC data
    const ssccData = await store.ssccs.get(ssccId);
    if (!ssccData) {
      alert(`SSCC (${ssccId}) not found in the system`, 'error');
      return false;
    }

    // Verify receiptedEvent exists at the specified index
    if (!ssccData.receiptedEvents || !ssccData.receiptedEvents[receiptedEventIndex]) {
      alert(`Receipt event not found at index ${receiptedEventIndex}`, 'error');
      return false;
    }

    // Check if ASN already exists in this receipt event
    const existingPackage = ssccData.receiptedEvents[receiptedEventIndex].packages?.find(
      (pkg) => normalizeAsnForComparison(pkg.asn) === normalizedTargetAsn || pkg.id === asn.id
    );
    if (existingPackage) {
      alert(`ASN ${asn.asn || asn.id} already exists in this receipt event`, 'error');
      return false;
    }

    const updatedASN = { ...asn, status: asnStatus, statusDate: dayjs() };

    // Update SSCC data - add ASN to the specified receipt event
    const updatedSSCC = {
      ...ssccData,
      receiptedEvents: ssccData.receiptedEvents.map((event: ReceiptEventSiteProps, index: number) => {
        if (index === receiptedEventIndex) {
          return {
            ...event,
            packages: [...(event.packages || []), updatedASN]
          };
        }
        return event;
      })
    };

    // Save SSCC changes
    await store.ssccs.update(updatedSSCC);

    // Capture after state for audit
    const afterState = buildAuditSnapshot({
      entityType: EntityType.ASN,
      entityId: updatedASN.asn || updatedASN.id,
      ssccId,
      asnId: updatedASN.asn || updatedASN.id,
      status: asnStatus,
      changesSummary: `Added ASN ${updatedASN.asn || updatedASN.id} to unassigned SSCC ${ssccId}`,
      extra: {
        receiptedEventIndex,
        isUnassigned: true,
        added: true
      }
    });

    // Log audit action
    await recordAdminAction({
      actionType: AdminActionType.ASN_ASSIGN_TO_SSCC,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error adding ASN to unassigned SSCC:',
      error,
      fallback: 'Failed to add ASN to the SSCC. Please try again.'
    });
    return false;
  }
};

// 05A
export const markSSCCCompleted = async ({
  selectedManifestSSCC,
  actionReason,
  ssccWeight,
  tripReference,
  alert,
  store,
  user,
  onlySendToCoupa
}: {
  selectedManifestSSCC: SelectedManifestSSCC;
  actionReason: string;
  ssccWeight?: string;
  tripReference: string | undefined;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
  onlySendToCoupa: boolean;
}): Promise<boolean> => {
  const existingSsccWeight = selectedManifestSSCC.sscc?.sscc?.weight;
  const normalizedSsccWeight = ssccWeight?.trim();
  const resolvedSsccWeight = existingSsccWeight || normalizedSsccWeight || undefined;

  if (!tripReference) {
    alert(`Trip reference is missing`, 'error');
    return false;
  }

  const tripData = await store.tripSheets.get(tripReference);

  if (!tripData) {
    alert(`Trip (${tripReference}) not found in the system`, 'error');
    return false;
  }

  const legIndex = tripData.legs.findIndex((leg) => leg.id === selectedManifestSSCC.legId) ?? -1;

  const originalManifestFromTrip = tripData.legs[legIndex]?.manifests?.find(
    (tempManifest) => tempManifest.id === selectedManifestSSCC.manifest.id
  );

  if (!originalManifestFromTrip) {
    alert(`Manifest (${selectedManifestSSCC.manifest.id}) not found in the system`, 'error');
    return false;
  }

  const beforeSsccStatus =
    toAuditStatus(selectedManifestSSCC.sscc?.sscc?.status) ||
    toAuditStatus(selectedManifestSSCC.ssccStatus) ||
    SsccStatus.open;
  const afterSsccStatus = SsccStatus.delivered;

  const ssccAsnStatusChanges = Array.from(
    new Map(
      (selectedManifestSSCC.sscc?.asns || [])
        .filter((asn) => Boolean(asn?.asn))
        .map((asn) => [
          asn.asn as string,
          {
            asn: asn.asn,
            beforeStatus: toAuditStatus(asn.status) || AsnStatus.open,
            afterStatus: AsnStatus.available
          }
        ])
    ).values()
  );

  const beforeState = buildAuditSnapshot({
    entityType: 'SSCC',
    entityId: selectedManifestSSCC.ssccId,
    tripId: selectedManifestSSCC.tripId,
    legId: selectedManifestSSCC.legId,
    manifestId: selectedManifestSSCC.manifest.id,
    ssccId: selectedManifestSSCC.ssccId,
    status: beforeSsccStatus,
    changesSummary: `SSCC ${selectedManifestSSCC.ssccId} completion started in manifest ${selectedManifestSSCC.manifest.id}`,
    extra: {
      tripDisplayStatus: selectedManifestSSCC.tripDisplayStatus,
      tripReference: tripData?.reference || selectedManifestSSCC.tripId,
      legIndex,
      ssccWeight: existingSsccWeight ?? null,
      asnStatusChanges: ssccAsnStatusChanges
    }
  });

  const manifestForSendingToCoupa = {
    ...originalManifestFromTrip,
    ssccs: originalManifestFromTrip.ssccs
      .filter((s) => s.ssccId === selectedManifestSSCC.ssccId)
      .map((s) => {
        return {
          ...s,
          ...(s.ssccId === selectedManifestSSCC.ssccId && resolvedSsccWeight
            ? { totalWeight: Number(resolvedSsccWeight) }
            : {}),
          packages: s.packages.map((p) => {
            if (p.receivedStatus === ManifestPackageStatus.notReceived) {
              return {
                ...p,
                quantity: p.maxQuantity,
                receivedStatus: ManifestPackageStatus.received
              };
            }

            return p;
          })
        };
      })
  };

  const updatedManifest = {
    ...originalManifestFromTrip,
    ssccs: originalManifestFromTrip.ssccs.map((s) => {
      if (s.ssccId === selectedManifestSSCC.ssccId) {
        return {
          ...s,
          ...(resolvedSsccWeight ? { totalWeight: Number(resolvedSsccWeight) } : {}),
          packages: s.packages.map((p) => {
            if (p.receivedStatus === ManifestPackageStatus.notReceived) {
              return {
                ...p,
                quantity: p.maxQuantity,
                receivedStatus: ManifestPackageStatus.received
              };
            }

            return p;
          })
        };
      }
      return s;
    })
  };

  try {
    alert('Submitting to Coupa', 'info');

    const reformattedManifest = {
      id: manifestForSendingToCoupa.id,
      status: manifestForSendingToCoupa.status,
      tripId: manifestForSendingToCoupa.tripId,
      trailerId: manifestForSendingToCoupa.trailerId,
      ssccs: manifestForSendingToCoupa.ssccs
    };

    const receiptedItems = mapToReceiptedItems(reformattedManifest) || [];

    for (const receiptedItem of receiptedItems) {
      const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
      if (receiptResponse.error) {
        alert(COUPA_RECEIPT_ERROR_MESSAGE, 'error');
        console.error(receiptResponse.error);
        return false;
      }
    }

    const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];

    const event: UpdateAsnProps = {
      asns: uniqueAsns.filter((asn): asn is string => asn !== undefined && asn !== null && asn !== variousAsn)
    };

    if (event.asns.length > 0) {
      await updateManifestAsnsCoupa(event);
    }

    !onlySendToCoupa && alert('Saving changes', 'info');

    !onlySendToCoupa &&
      (await onSaveChangesToTripSheet({
        manifest: updatedManifest,
        tripData,
        alert,
        store,
        triggeredBy: user.email,
        context: 'markSsccCompleted'
      }));

    !onlySendToCoupa &&
      (await handleSsccAsnStatusUpdateForSSCC({
        ssccId: selectedManifestSSCC.ssccId,
        weight: resolvedSsccWeight,
        store
      }));

    alert('Manifest submitted successfully', 'success');

    const afterState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: selectedManifestSSCC.ssccId,
      tripId: selectedManifestSSCC.tripId,
      legId: selectedManifestSSCC.legId,
      manifestId: updatedManifest.id,
      ssccId: selectedManifestSSCC.ssccId,
      status: afterSsccStatus,
      changesSummary: `SSCC ${selectedManifestSSCC.ssccId} status ${beforeSsccStatus} -> ${afterSsccStatus} in manifest ${updatedManifest.id}`,
      extra: {
        inventoryTransfer: true,
        manifestStatus: updatedManifest.status,
        asns: extractASNsFromManifest({ ...selectedManifestSSCC.manifest, ssccs: updatedManifest.ssccs }),
        tripReference: tripData?.reference || selectedManifestSSCC.tripId,
        legIndex,
        ssccWeight: resolvedSsccWeight ?? null,
        asnStatusChanges: ssccAsnStatusChanges
      }
    });

    !onlySendToCoupa &&
      (await recordAdminAction({
        actionType: AdminActionType.SSCC_COMPLETE,
        beforeAction: beforeState,
        afterAction: afterState,
        actionBy: user.email,
        reason: actionReason
      }));

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error completing SSCC:',
      error,
      fallback: 'Failed to complete the SSCC. Please try again.'
    });
    return false;
  }
};

// 05B
export const markUnassignedSSCCCompleted = async ({
  sscc,
  actionReason,
  ssccWeight,
  alert,
  store,
  user
}: {
  sscc: SSCCProps | undefined;
  actionReason: string;
  ssccWeight?: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  const normalizedSsccWeight = ssccWeight?.trim();
  const resolvedSsccWeight = normalizedSsccWeight || sscc?.weight || undefined;

  const asnStatusChanges = Array.from(
    new Map(
      (sscc?.receiptedEvents || [])
        .flatMap((event) => event.packages || [])
        .filter((pkg) => Boolean(pkg?.asn))
        .map((pkg) => [
          pkg.asn as string,
          {
            asn: pkg.asn,
            beforeStatus: toAuditStatus(pkg.status) || AsnStatus.open,
            afterStatus: AsnStatus.available
          }
        ])
    ).values()
  );

  const beforeState = buildAuditSnapshot({
    entityType: EntityType.SSCC,
    entityId: sscc?.id || 'unknown',
    ssccId: sscc?.id || null,
    status: sscc?.status,
    changesSummary: `Unassigned SSCC ${sscc?.id || 'unknown'} completion started`,
    extra: {
      ssccWeight: sscc?.weight ?? null,
      asnStatusChanges
    }
  });

  if (!sscc) {
    alert('SSCC not found', 'error');
    return false;
  }

  const flatMapPackagesAndAsnLinesResult = flatMapPackagesAndAsnLines({ originalSSCC: sscc, inventoryTransfer: true });

  const manifestForSendingToCoupa = {
    ssccs: [
      {
        ssccId: sscc.id,
        packages: flatMapPackagesAndAsnLinesResult,
        totalWeight: Number(resolvedSsccWeight)
      }
    ]
  };

  try {
    alert('Submitting to Coupa', 'info');

    const reformattedManifest = {
      id: '',
      status: ManifestStatus.open,
      tripId: '',
      trailerId: '',
      ssccs: manifestForSendingToCoupa.ssccs
    };

    const receiptedItems = mapToReceiptedItems(reformattedManifest) || [];

    for (const receiptedItem of receiptedItems) {
      const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
      if (receiptResponse.error) {
        alert(COUPA_RECEIPT_ERROR_MESSAGE, 'error');
        console.error(receiptResponse.error);
        return false;
      }
    }

    const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];

    const event: UpdateAsnProps = {
      asns: uniqueAsns.filter((asn): asn is string => asn !== undefined && asn !== null && asn !== variousAsn)
    };

    if (event.asns.length > 0) {
      await updateManifestAsnsCoupa(event);
    }

    alert('Saving changes', 'info');

    await handleSsccAsnStatusUpdateForSSCC({
      ssccId: sscc.id,
      weight: resolvedSsccWeight,
      store,
      makeSsccDelivered: true
    });

    alert('SSCC submitted successfully', 'success');

    const afterState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: sscc.id,
      ssccId: sscc.id,
      status: SsccStatus.delivered,
      changesSummary: `Unassigned SSCC ${sscc.id} marked delivered`,
      extra: {
        inventoryTransfer: true,
        ssccWeight: resolvedSsccWeight ?? null,
        asnStatusChanges
      }
    });

    await recordAdminAction({
      actionType: AdminActionType.SSCC_COMPLETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error completing unassigned SSCC:',
      error,
      fallback: 'Failed to complete the SSCC. Please try again.'
    });
    return false;
  }
};

// 06
export const deleteSSCC = async ({
  tripId,
  legId,
  ssccId,
  actionReason,
  alert,
  store,
  user
}: {
  tripId: string;
  legId: string;
  ssccId: string;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate all required parameters
  if (!tripId) {
    alert('Trip information is missing', 'error');
    return false;
  }

  if (!legId) {
    alert('Leg information is missing', 'error');
    return false;
  }

  if (!ssccId) {
    alert('SSCC identifier is missing', 'error');
    return false;
  }

  try {
    // Fetch trip data
    const tripData = await store.tripSheets.get(tripId);
    if (!tripData) {
      alert(`Trip (${tripId}) not found in the system`, 'error');
      return false;
    }

    // Verify leg exists
    const legIndex = tripData.legs.findIndex((leg) => leg.id === legId);
    if (legIndex === -1) {
      alert(`Leg (${legId}) not found in this trip`, 'error');
      return false;
    }

    // Verify SSCC exists in the specified leg
    const leg = tripData.legs[legIndex];
    const manifestContainingSscc = leg.manifests.find((manifest) =>
      manifest.ssccs.some((sscc) => sscc.ssccId === ssccId)
    );

    if (!manifestContainingSscc) {
      alert(`SSCC not found in this leg. It may have already been deleted.`, 'error');
      return false;
    }

    const ssccData = await store.ssccs.get(ssccId);
    if (!ssccData) {
      alert(`SSCC (${ssccId}) not found in the system`, 'error');
      return false;
    }

    const ssccBeforeStatus = toAuditStatus(ssccData.status) || SsccStatus.open;
    const ssccAfterStatus = SsccStatus.staged;

    const asnStatusChangesMap = (ssccData.receiptedEvents || [])
      .flatMap((event) => event.packages || [])
      .reduce<Record<string, { asn: string; beforeStatus: string; afterStatus: AsnStatus }>>((acc, pkg) => {
        const asnId = pkg?.asn || pkg?.id;
        if (!asnId) {
          return acc;
        }

        acc[asnId] = {
          asn: asnId,
          beforeStatus: toAuditStatus(pkg.status) || AsnStatus.open,
          afterStatus: AsnStatus.staged
        };

        return acc;
      }, {});

    const asnStatusChanges = Object.values(asnStatusChangesMap);

    const beforeState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: ssccId,
      tripId,
      legId,
      manifestId: manifestContainingSscc.id,
      ssccId,
      status: ssccBeforeStatus,
      changesSummary: `SSCC ${ssccId} removal from manifest ${manifestContainingSscc.id} started`,
      extra: {
        tripReference: tripData.reference || tripId,
        legIndex,
        asnStatusChanges
      }
    });

    // Update trip data - remove SSCC from manifest's SSCCs
    const updatedTripData = {
      ...tripData,
      legs: tripData.legs.map((currentLeg, index) => {
        if (index === legIndex) {
          return {
            ...currentLeg,
            manifests: currentLeg.manifests.map((manifest) => ({
              ...manifest,
              ssccs: manifest.ssccs.filter((sscc) => sscc.ssccId !== ssccId)
            }))
          };
        }
        return currentLeg;
      })
    };

    const updatedSSCCData = {
      ...ssccData,
      status: SsccStatus.staged,
      staged: dayjs(),
      manifested: null,
      inTransit: null,
      approaching: null,
      delivered: null,
      receiptedEvents: ssccData.receiptedEvents.map((event: ReceiptEventSiteProps) => ({
        ...event,
        packages: event.packages.map((pkg: any) => ({
          ...pkg,
          status: AsnStatus.staged,
          statusDate: dayjs()
        }))
      }))
    };

    // Save changes to database
    await store.tripSheets.update(updatedTripData);
    const syncedManifest = findManifestInTripSheet(updatedTripData, manifestContainingSscc.id);
    if (syncedManifest) {
      void syncAdminManifestSnapshot({
        tripData: updatedTripData,
        manifest: syncedManifest,
        triggeredBy: user.email,
        context: 'removeSsccFromManifest'
      });
    }

    await store.ssccs.update(updatedSSCCData);

    // Log the action
    const afterState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: ssccId,
      tripId,
      legId,
      manifestId: manifestContainingSscc.id,
      ssccId,
      status: ssccAfterStatus,
      changesSummary: `Removed SSCC ${ssccId} from manifest ${manifestContainingSscc.id} and reset ASN statuses to staged`,
      extra: {
        deleted: true,
        tripReference: tripData.reference || tripId,
        legIndex,
        asnStatusChanges
      }
    });

    await recordAdminAction({
      actionType: AdminActionType.SSCC_REMOVE_FROM_MANIFEST,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error deleting SSCC:',
      error,
      fallback: 'Failed to delete the SSCC. Please try again.'
    });
    return false;
  }
};

// 06B
export const deleteUnassignedSSCC = async ({
  ssccId,
  actionReason,
  makeAsnAvailable = true,
  alert,
  store,
  user
}: {
  ssccId: string;
  actionReason: string;
  makeAsnAvailable?: boolean;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate all required parameters

  if (!ssccId) {
    alert('SSCC identifier is missing', 'error');
    return false;
  }

  if (!actionReason?.trim()) {
    alert('Reason for cancellation is required', 'error');
    return false;
  }

  try {
    const ssccData = await store.ssccs.get(ssccId);
    if (!ssccData) {
      alert(`SSCC (${ssccId}) not found in the system`, 'error');
      return false;
    }

    const assignedStatuses = [
      SsccStatus.manifested,
      SsccStatus.inTransit,
      SsccStatus.approaching,
      SsccStatus.delivered,
      SsccStatus.cancelled
    ];
    if (assignedStatuses.includes(ssccData.status as SsccStatus)) {
      alert(`Cannot delete SSCC (${ssccId}) — it is currently ${ssccData.status}`, 'error');
      return false;
    }

    const ssccBeforeStatus = toAuditStatus(ssccData.status) || SsccStatus.open;
    const ssccAfterStatus = SsccStatus.cancelled;
    const receiptedEvents = ssccData.receiptedEvents || [];

    const asnStatusChangesMap = receiptedEvents
      .flatMap((event) => event.packages || [])
      .reduce<Record<string, { asn: string; beforeStatus: string; afterStatus: AsnStatus }>>((acc, pkg) => {
        const asnId = pkg?.asn || pkg?.id;
        if (!asnId) {
          return acc;
        }

        acc[asnId] = {
          asn: asnId,
          beforeStatus: toAuditStatus(pkg.status) || AsnStatus.open,
          afterStatus: AsnStatus.cancelled
        };

        return acc;
      }, {});

    const asnStatusChanges = Object.values(asnStatusChangesMap);

    const beforeState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: ssccId,
      tripId: null,
      legId: null,
      manifestId: null,
      ssccId,
      status: ssccBeforeStatus,
      changesSummary: `Unassigned SSCC ${ssccId} cancellation started; update ASN to available: ${makeAsnAvailable ? 'yes' : 'no'}`,
      extra: {
        isUnassigned: true,
        updateAsnToAvailable: makeAsnAvailable,
        asnStatusChanges
      }
    });

    const uniqueAsns = [
      ...new Set(
        receiptedEvents
          .flatMap((event) => event.packages || [])
          .map((pkg) => (typeof pkg?.asn === 'string' ? pkg.asn.trim() : pkg?.asn))
          .filter((asn): asn is string => asn !== undefined && asn !== null && asn !== '' && asn !== variousAsn)
      )
    ];

    if (makeAsnAvailable && uniqueAsns.length > 0) {
      const event: UpdateAsnProps = {
        asns: uniqueAsns
      };

      await updateManifestAsnsCoupa(event);
    }

    const updatedSSCCData = {
      ...ssccData,
      status: SsccStatus.cancelled,
      receiptedEvents: receiptedEvents.map((event: ReceiptEventSiteProps) => ({
        ...event,
        packages: (event.packages || []).map((pkg: any) => ({
          ...pkg,
          status: AsnStatus.cancelled,
          statusDate: dayjs()
        }))
      }))
    };

    await store.ssccs.update(updatedSSCCData);

    // Log the action
    const afterState = buildAuditSnapshot({
      entityType: EntityType.SSCC,
      entityId: ssccId,
      tripId: null,
      legId: null,
      manifestId: null,
      ssccId,
      status: ssccAfterStatus,
      changesSummary: `Unassigned SSCC ${ssccId} cancelled and ASN statuses set to cancelled; update ASN to available: ${makeAsnAvailable ? 'yes' : 'no'}`,
      extra: {
        deleted: true,
        isUnassigned: true,
        updateAsnToAvailable: makeAsnAvailable,
        coupaAsnsUpdated: makeAsnAvailable ? uniqueAsns.length : 0,
        asnStatusChanges
      }
    });

    await recordAdminAction({
      actionType: AdminActionType.SSCC_DELETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error deleting SSCC:',
      error,
      fallback: 'Failed to cancel the SSCC. Please try again.'
    });
    return false;
  }
};

// 07
export const deleteASN = async ({
  asnUniqueId,
  tripAsnUniqueId,
  asnId,
  asnPoNumber,
  isDuplicate,
  tripId,
  legId,
  ssccId,
  priorityShipping,
  actionReason,
  alert,
  store,
  user
}: {
  asnUniqueId: string;
  tripAsnUniqueId?: string | null;
  asnId: string;
  asnPoNumber?: string | null;
  isDuplicate: boolean;
  tripId: string | null;
  legId: string | null;
  ssccId: string | null;
  priorityShipping?: boolean;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  // Validate all required parameters
  if (!asnUniqueId) {
    alert('ASN identifier is missing', 'error');
    return false;
  }

  if (!asnId) {
    alert('ASN ID is missing', 'error');
    return false;
  }

  if (!ssccId) {
    alert('SSCC information is missing', 'error');
    return false;
  }

  if (!actionReason?.trim()) {
    alert('Reason for deletion is required', 'error');
    return false;
  }

  // Check if this is an unassigned SSCC (not in any trip/leg/manifest)
  const isUnassignedSSCC = !tripId || !legId;

  try {
    let tripData: TripSheetProps | undefined;
    let tripReference: string | null = null;
    let legIndex: number | null = null;
    let manifestId: string | null = null;
    const normalizedAsnId = normalizeAsnForComparison(asnId);

    if (!isUnassignedSSCC && tripId && legId) {
      tripData = await store.tripSheets.get(tripId);
      if (!tripData) {
        alert(`Trip (${tripId}) not found in the system`, 'error');
        return false;
      }

      legIndex = tripData.legs.findIndex((leg) => leg.id === legId);
      if (legIndex === -1) {
        alert(`Leg (${legId}) not found in this trip`, 'error');
        return false;
      }

      tripReference = tripData.reference || tripId;
      const leg = tripData.legs[legIndex];
      manifestId = leg.manifests.find((manifest) => manifest.ssccs.some((sscc) => sscc.ssccId === ssccId))?.id || null;

      if (!manifestId) {
        alert(`SSCC (${ssccId}) was not found in any manifest for this leg`, 'error');
        return false;
      }
    }

    const beforeState = buildAuditSnapshot({
      entityType: EntityType.ASN,
      entityId: asnId,
      tripId,
      legId,
      manifestId,
      ssccId,
      asnId,
      changesSummary: `ASN ${asnId} removal started from SSCC ${ssccId}`,
      extra: {
        asnUniqueId,
        isUnassigned: isUnassignedSSCC,
        tripReference,
        legIndex,
        priorityShipping: Boolean(priorityShipping)
      }
    });

    // Fetch SSCC data
    const ssccData = await store.ssccs.get(ssccId);
    if (!ssccData) {
      alert(`SSCC (${ssccId}) not found in the system`, 'error');
      return false;
    }

    const isMatchingSsccPackage = (pkg: ReceiptEventPackageProps) => {
      const idMatches = pkg.id === asnUniqueId;
      const asnMatches = normalizeAsnForComparison(pkg.asn) === normalizedAsnId;
      const poMatches = !asnPoNumber || (pkg.po || '') === asnPoNumber;
      return idMatches || (asnMatches && poMatches);
    };

    const ssccMatchingCount = (ssccData.receiptedEvents || [])
      .flatMap((event) => event.packages || [])
      .filter((pkg) => isMatchingSsccPackage(pkg)).length;

    if (ssccMatchingCount === 0) {
      alert(`Cannot delete ASN ${asnId}. SSCC has 0 matching records.`, 'error');
      return false;
    }

    let updatedTripData: TripSheetProps | undefined;
    let tripRemovedCount = 0;
    let removeFromTripCount = 0;
    let removeFromSsccCount = 0;

    // Update trip data only if SSCC is assigned to a manifest
    if (!isUnassignedSSCC && tripId && legId && tripData && legIndex !== null && legIndex >= 0) {
      const isMatchingTripPackage = (pkg: {
        id?: string;
        asn?: string;
        poNumber?: string;
        inventoryLines?: unknown[];
      }) => {
        // Trip package ids are distinct from SSCC receipted event package ids.
        const targetTripId = tripAsnUniqueId || null;
        const idMatches = Boolean(targetTripId && pkg.id === targetTripId);
        const asnMatches = normalizeAsnForComparison(pkg.asn) === normalizedAsnId;
        const poMatches = !asnPoNumber || (pkg.poNumber || '') === asnPoNumber;
        const hasEmptyInventoryLines = Array.isArray(pkg.inventoryLines) && pkg.inventoryLines.length === 0;

        // For trip packages with no inventory lines, match by ASN only.
        const asnOnlyMatchForEmptyInventoryLines = hasEmptyInventoryLines && asnMatches;

        return idMatches || (asnMatches && poMatches) || asnOnlyMatchForEmptyInventoryLines;
      };

      const tripMatchingCount = tripData.legs[legIndex].manifests
        .flatMap((manifest) => manifest.ssccs.filter((sscc) => sscc.ssccId === ssccId))
        .flatMap((sscc) => sscc.packages || [])
        .filter((pkg) => isMatchingTripPackage(pkg)).length;

      if (tripMatchingCount === 0) {
        alert(`Cannot delete ASN ${asnId}. Trip has 0 matching records.`, 'error');
        return false;
      }

      if (isDuplicate) {
        // Duplicate case: keep exactly one on each side.
        removeFromSsccCount = Math.max(ssccMatchingCount - 1, 0);
        removeFromTripCount = Math.max(tripMatchingCount - 1, 0);
      } else {
        // Non-duplicate case: remove all on each side.
        removeFromSsccCount = ssccMatchingCount;
        removeFromTripCount = tripMatchingCount;
      }

      updatedTripData = {
        ...tripData,
        legs: tripData!.legs.map((leg, index) => {
          if (index === legIndex!) {
            return {
              ...leg,
              manifests: leg.manifests.map((manifest) => ({
                ...manifest,
                ssccs: manifest.ssccs.map((sscc) => {
                  if (sscc.ssccId === ssccId) {
                    const packages = sscc.packages || [];

                    const updatedPackages = (() => {
                      if (!isDuplicate) {
                        let remainingTripRemovals = removeFromTripCount;
                        return packages.filter((pkg) => {
                          if (remainingTripRemovals <= 0) {
                            return true;
                          }
                          if (isMatchingTripPackage(pkg)) {
                            remainingTripRemovals -= 1;
                            tripRemovedCount += 1;
                            return false;
                          }
                          return true;
                        });
                      }

                      const prioritizedTripRemovals = packages
                        .map((pkg, packageIndex) => ({
                          pkg,
                          packageIndex,
                          isMatch: isMatchingTripPackage(pkg),
                          hasEmptyInventoryLines: Array.isArray(pkg.inventoryLines) && pkg.inventoryLines.length === 0
                        }))
                        .filter((entry) => entry.isMatch)
                        .sort((left, right) => {
                          const leftPriority = left.hasEmptyInventoryLines ? 0 : 1;
                          const rightPriority = right.hasEmptyInventoryLines ? 0 : 1;
                          return leftPriority - rightPriority;
                        })
                        .slice(0, removeFromTripCount);

                      const removalIndexes = new Set(prioritizedTripRemovals.map((entry) => entry.packageIndex));
                      tripRemovedCount += removalIndexes.size;

                      return packages.filter((_, packageIndex) => !removalIndexes.has(packageIndex));
                    })();

                    return {
                      ...sscc,
                      packages: updatedPackages
                    };
                  }
                  return sscc;
                })
              }))
            };
          }
          return leg;
        })
      };
    }

    if (isUnassignedSSCC) {
      // Unassigned SSCC has no trip-side source of truth.
      // Duplicate mode: remove extras and leave one.
      // Non-duplicate mode: remove all matching entries.
      removeFromSsccCount = isDuplicate ? Math.max(ssccMatchingCount - 1, 0) : ssccMatchingCount;
    }

    // Update SSCC data - remove ASN from receipted events
    let ssccRemovedCount = 0;
    let remainingSsccRemovals = removeFromSsccCount;
    const updatedSSCCData = {
      ...ssccData,
      receiptedEvents: ssccData.receiptedEvents.map((event: ReceiptEventSiteProps) => {
        const packages = event.packages || [];
        const updatedPackages = packages.filter((pkg) => {
          if (remainingSsccRemovals <= 0) {
            return true;
          }
          if (isMatchingSsccPackage(pkg)) {
            remainingSsccRemovals -= 1;
            ssccRemovedCount += 1;
            return false;
          }
          return true;
        });

        if (updatedPackages.length === packages.length) {
          return event;
        }

        return {
          ...event,
          packages: updatedPackages
        };
      })
    };

    if (!isUnassignedSSCC) {
      if (tripRemovedCount !== removeFromTripCount || ssccRemovedCount !== removeFromSsccCount) {
        alert(
          `ASN removal validation failed. Planned removals - Trip: ${removeFromTripCount}, SSCC: ${removeFromSsccCount}. Actual removals - Trip: ${tripRemovedCount}, SSCC: ${ssccRemovedCount}. No changes were saved.`,
          'error'
        );
        return false;
      }
    } else if (ssccRemovedCount !== removeFromSsccCount) {
      alert(
        `ASN removal validation failed for SSCC ${ssccId}. Planned removals: ${removeFromSsccCount}, actual removals: ${ssccRemovedCount}.`,
        'error'
      );
      return false;
    }

    if (updatedTripData) {
      await store.tripSheets.update(updatedTripData);
      const syncedManifest = findManifestInTripSheet(updatedTripData, manifestId);
      if (syncedManifest) {
        void syncAdminManifestSnapshot({
          tripData: updatedTripData,
          manifest: syncedManifest,
          triggeredBy: user.email,
          context: 'removeAsnFromSscc'
        });
      }
    }

    // Save SSCC changes to database
    await store.ssccs.update(updatedSSCCData);

    // Log the action
    const afterState = buildAuditSnapshot({
      entityType: EntityType.ASN,
      entityId: asnId,
      tripId,
      legId,
      manifestId,
      ssccId,
      asnId,
      status: 'Deleted',
      changesSummary: `Removed ASN ${asnId} (${asnUniqueId}) from SSCC ${ssccId}`,
      extra: {
        asnUniqueId,
        isUnassigned: isUnassignedSSCC,
        tripReference,
        legIndex,
        deleted: true,
        priorityShipping: Boolean(priorityShipping)
      }
    });

    await recordAdminAction({
      actionType: AdminActionType.ASN_REMOVE_FROM_SSCC,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error deleting ASN:',
      error,
      fallback: 'Failed to delete the ASN. Please try again.'
    });
    return false;
  }
};

// 08
export const inventoryTransferASN = async ({
  targetSsccId,
  targetSsccWeight,
  targetReceiptedEventName,
  targetReceiptedEventSite,
  asn,
  alert
}: {
  targetSsccId: string;
  targetSsccWeight?: string | null;
  targetReceiptedEventName?: string | null;
  targetReceiptedEventSite?: string | null;
  asn: ReceiptEventPackageProps;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
}): Promise<boolean> => {
  if (!targetSsccId) {
    alert('Target SSCC is missing for inventory transfer.', 'error');
    return false;
  }

  if (!asn || (!asn.id && !asn.asn)) {
    alert('ASN information is missing for inventory transfer.', 'error');
    return false;
  }

  try {
    const wrappedSSCC = {
      id: targetSsccId,
      weight: targetSsccWeight || '0',
      receiptedEvents: [
        {
          name: targetReceiptedEventName ?? null,
          site: targetReceiptedEventSite ?? null,
          packages: [asn]
        }
      ]
    } as unknown as SSCCProps;

    const wrappedPackages = flatMapPackagesAndAsnLines({ originalSSCC: wrappedSSCC, inventoryTransfer: true });

    const manifestForSendingToCoupa = {
      id: '',
      status: ManifestStatus.open,
      tripId: '',
      trailerId: '',
      ssccs: [
        {
          ssccId: targetSsccId,
          totalWeight: Number(targetSsccWeight || 0),
          packages: wrappedPackages
        }
      ]
    };

    const receiptedItems = mapToReceiptedItems(manifestForSendingToCoupa);

    for (const receiptedItem of receiptedItems) {
      const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
      if (receiptResponse.error) {
        alert(COUPA_RECEIPT_ERROR_MESSAGE, 'error');
        console.error(receiptResponse.error);
        return false;
      }
    }

    const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];

    const event: UpdateAsnProps = {
      asns: uniqueAsns.filter((asnId): asnId is string => asnId !== undefined && asnId !== null && asnId !== variousAsn)
    };

    if (event.asns.length > 0) {
      await updateManifestAsnsCoupa(event);
    }

    return true;
  } catch (error) {
    alertUnexpectedError({
      alert,
      context: 'Error transferring ASN inventory:',
      error,
      fallback: COUPA_RECEIPT_ERROR_MESSAGE
    });
    return false;
  }
};
